import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  {
    key: "problem_type",
    title: "What kind of friction are you feeling?",
    subtitle: "Pick the closest match — we'll refine from there.",
    options: [
      { v: "onboarding", label: "Messy onboarding", hint: "Forms, docs, chase emails" },
      { v: "support", label: "Support chaos", hint: "Inbox, SLAs, dropped replies" },
      { v: "reporting", label: "Reporting spaghetti", hint: "Spreadsheets on spreadsheets" },
      { v: "conversion", label: "Conversion leaks", hint: "Visits without bookings" },
      { v: "operations", label: "Operational drag", hint: "Manual, repetitive work" },
    ],
  },
  {
    key: "user_type",
    title: "Who will actually use the thing?",
    subtitle: "The person whose day it needs to fit.",
    options: [
      { v: "founder", label: "Founder / CEO" },
      { v: "ops", label: "Operations lead" },
      { v: "sales", label: "Sales / success" },
      { v: "analyst", label: "Analyst / finance" },
      { v: "field", label: "Field / frontline" },
    ],
  },
  {
    key: "desired_outcome",
    title: "What would 'success' look like in 90 days?",
    subtitle: "One sentence, honest answer.",
    options: [
      { v: "faster", label: "Stop losing time" },
      { v: "revenue", label: "More revenue through the door" },
      { v: "visibility", label: "Actually see what's happening" },
      { v: "retention", label: "Keep customers / clients longer" },
      { v: "automation", label: "Automate the boring" },
    ],
  },
  {
    key: "urgency",
    title: "How urgent is this?",
    subtitle: "Be honest — it shapes what we propose.",
    options: [
      { v: "now", label: "Yesterday", hint: "Weeks matter" },
      { v: "quarter", label: "This quarter", hint: "Within 3 months" },
      { v: "exploring", label: "Still exploring", hint: "Curious, not committed" },
    ],
  },
];

export default function GuidedJourney() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [recommendations, setRecommendations] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const currentStep = STEPS[step];
  const isLastStep = step === STEPS.length - 1;

  const choose = async (value) => {
    const next = { ...answers, [currentStep.key]: value };
    setAnswers(next);
    if (!isLastStep) {
      setStep(step + 1);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/guided-journey", next);
      setRecommendations(data.recommendations || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not generate recommendations");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setStep(0);
    setRecommendations(null);
  };

  if (recommendations) {
    return (
      <div data-testid="guided-journey-recs">
        <section className="border-b border-zinc-200">
          <div className="app-container py-16 md:py-20">
            <p className="overline mb-4 text-[hsl(var(--appistry-signal))]">Match found</p>
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-none mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Three demos worth your time.
            </h1>
            <p className="text-lg text-zinc-600 max-w-2xl">
              Based on your answers, these are the builds closest to your situation. Open any one — or book a call to talk them through.
            </p>
          </div>
        </section>

        <section>
          <div className="app-container py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommendations.map((d, i) => (
              <Link
                key={d.id}
                to={`/demos/${d.slug}`}
                className="border border-zinc-200 bg-white p-6 group hover:-translate-y-1 hover:shadow-lg transition-all"
                data-testid={`recommendation-${i}`}
              >
                <p className="overline text-zinc-500 mb-2">{d.category}</p>
                <h3 className="text-xl font-semibold tracking-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{d.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed mb-4">{d.short_description}</p>
                <div className="text-sm font-semibold inline-flex items-center gap-1 text-[hsl(var(--appistry-signal))]">
                  Open demo <ArrowRight size={14} weight="bold" />
                </div>
              </Link>
            ))}
          </div>
          <div className="app-container pb-20 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="btn-signal" data-testid="recs-consultation-cta">
              See how we can help <ArrowRight size={16} weight="bold" />
            </Link>
            <button onClick={reset} className="btn-ghost-ink" data-testid="recs-restart">
              Start over
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="guided-journey-page">
      <section className="border-b border-zinc-200">
        <div className="app-container py-12">
          <div className="flex items-center justify-between mb-6">
            <p className="overline">Concept Matcher</p>
            <p className="text-xs font-mono text-zinc-500">Step {step + 1} / {STEPS.length}</p>
          </div>
          <div className="h-1 bg-zinc-200">
            <div className="h-1 bg-[hsl(var(--appistry-signal))] transition-all" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>
      </section>

      <section className="app-container py-16 md:py-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight mb-4 max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="journey-question">
              {currentStep.title}
            </h1>
            <p className="text-zinc-600 mb-10">{currentStep.subtitle}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentStep.options.map((o) => {
                const selected = answers[currentStep.key] === o.v;
                return (
                  <button
                    key={o.v}
                    onClick={() => choose(o.v)}
                    disabled={submitting}
                    className={`text-left p-6 border transition-all ${
                      selected ? "border-[hsl(var(--appistry-signal))] bg-white" : "border-zinc-200 bg-white hover:border-zinc-400"
                    }`}
                    data-testid={`journey-option-${o.v}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{o.label}</p>
                        {o.hint && <p className="text-sm text-zinc-500">{o.hint}</p>}
                      </div>
                      <CheckCircle size={22} weight={selected ? "fill" : "regular"} className={selected ? "text-[hsl(var(--appistry-signal))]" : "text-zinc-300"} />
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between">
              <button
                onClick={() => setStep(Math.max(0, step - 1))}
                disabled={step === 0}
                className="btn-ghost-ink disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="journey-back"
              >
                <ArrowLeft size={16} weight="bold" /> Back
              </button>
              {submitting && <p className="text-sm font-mono text-zinc-500">Finding matches…</p>}
            </div>
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}
